import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Send, Receipt, PiggyBank, Banknote, Users, CreditCard, ArrowUpRight, ArrowDownRight, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mockUser, mockTransactions, mockSavings, formatCurrency, formatDate } from '@/utils/mockData';

export default function DashboardHome() {
  const navigate = useNavigate();
  const [showBalance, setShowBalance] = React.useState(true);

  const quickActions = [
    { icon: <Send className="w-6 h-6" />, label: 'Transfer', path: '/dashboard/transfer', color: 'bg-[#221a83]' },
    { icon: <Receipt className="w-6 h-6" />, label: 'Pay Bills', path: '/dashboard/bills', color: 'bg-green-500' },
    { icon: <PiggyBank className="w-6 h-6" />, label: 'Save', path: '/dashboard/savings', color: 'bg-purple-500' },
    { icon: <Banknote className="w-6 h-6" />, label: 'Loans', path: '/dashboard/loans', color: 'bg-orange-500' },
    { icon: <Users className="w-6 h-6" />, label: 'Groups', path: '/dashboard/cooperatives', color: 'bg-pink-500' },
    { icon: <CreditCard className="w-6 h-6" />, label: 'Cards', path: '/dashboard/cards', color: 'bg-indigo-500' },
  ];

  const recentTransactions = mockTransactions.slice(0, 5);

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Welcome Banner */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h1 className="text-2xl md:text-3xl font-bold font-heading text-slate-900">
          Welcome back, {mockUser.firstName}! 👋
        </h1>
        <p className="text-slate-600 mt-1">Here's what's happening with your money today</p>
      </div>

      {/* Wallet Card */}
      <div className="gradient-card rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-indigo-100 text-sm mb-1">Total Balance</p>
              <div className="flex items-center gap-4">
                <h2 className="text-4xl md:text-5xl font-bold font-heading">
                  {showBalance ? formatCurrency(mockUser.walletBalance) : '••••••••'}
                </h2>
                <button 
                  onClick={() => setShowBalance(!showBalance)}
                  data-testid="toggle-balance-btn"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {showBalance ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                </button>
              </div>
            </div>
            <div className="text-right">
              <p className="text-indigo-100 text-sm mb-1">Account</p>
              <p className="text-lg font-semibold">{mockUser.accountNumber}</p>
              <p className="text-indigo-100 text-xs mt-1">{mockUser.tier} Tier</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <Button 
              onClick={() => navigate('/dashboard/transfer')}
              data-testid="wallet-add-money-btn"
              className="bg-white text-brand-blue hover:bg-indigo-50 rounded-full px-6 py-3 font-semibold transition-all active:scale-95"
            >
              Add Money
            </Button>
            <Button 
              onClick={() => navigate('/dashboard/transfer')}
              data-testid="wallet-send-money-btn"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-3 font-semibold transition-all"
            >
              Send Money
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-bold font-heading text-slate-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {quickActions.map((action, idx) => (
            <button
              key={idx}
              onClick={() => navigate(action.path)}
              data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}-btn`}
              className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 hover:border-brand-blue/50 hover:shadow-md transition-all group"
            >
              <div className={`${action.color} text-white w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                {action.icon}
              </div>
              <p className="text-sm font-medium text-slate-900">{action.label}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold font-heading text-slate-900">Recent Transactions</h3>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard/transactions')}
              data-testid="view-all-transactions-btn"
              className="text-brand-blue hover:text-[#1a1466]"
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {recentTransactions.map((txn) => (
              <div 
                key={txn.id} 
                className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors"
                data-testid={`transaction-${txn.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  txn.type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                  {txn.type === 'credit' ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{txn.description}</p>
                  <p className="text-sm text-slate-600">{formatDate(txn.date)}</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                    {txn.type === 'credit' ? '+' : '-'}{formatCurrency(txn.amount)}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">{txn.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Savings Goals */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold font-heading text-slate-900">Savings Goals</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/dashboard/savings')}
              data-testid="view-all-savings-btn"
              className="text-brand-blue"
            >
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {mockSavings.slice(0, 3).map((saving) => {
              const progress = (saving.current / saving.target) * 100;
              return (
                <div key={saving.id} className="space-y-2" data-testid={`saving-goal-${saving.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-900 text-sm">{saving.name}</p>
                    <p className="text-xs text-slate-600">{progress.toFixed(0)}%</p>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-brand-blue rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span>{formatCurrency(saving.current)}</span>
                    <span>{formatCurrency(saving.target)}</span>
                  </div>
                </div>
              );
            })}
            <Button 
              onClick={() => navigate('/dashboard/savings')}
              data-testid="create-savings-goal-btn"
              className="w-full bg-brand-blue text-white hover:bg-[#1a1466] rounded-xl py-3 mt-4 transition-all active:scale-95"
            >
              Create New Goal
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Income</p>
              <p className="text-2xl font-bold font-heading text-slate-900">₦83,000</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 rotate-180" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Expenses</p>
              <p className="text-2xl font-bold font-heading text-slate-900">₦38,000</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 text-brand-blue flex items-center justify-center">
              <PiggyBank className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Savings</p>
              <p className="text-2xl font-bold font-heading text-slate-900">₦2,080,000</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
